import React from "react";
import {
  Building2,
  Loader2,
  RefreshCw,
} from "lucide-react";

/**
 * Contract filter dropdown content for use with FilterButton
 * Props:
 * - filters: {
 *     offices: string[],
 *     statuses: string[],
 *     query: string
 *   }
 * - setFilters: updater for filters state
 * - offices: array of office codes (strings)
 * - statusOptions: array of status values (strings)
 * - onApply: callback(filters | null) invoked on apply or reset
 * - operationDate: string (YYYY-MM-DD) to display alongside search
 * - isRefreshing: boolean to show loader near offices
 */
const ContractDropdownContent = ({
  filters,
  setFilters,
  offices = [],
  statusOptions = ["DRAFT", "ACTIVE", "EXPIRED"],
  onApply,
  operationDate,
  refreshingOffices = [],
}) => {
  const formatToDDMMYYYY = (dateInput) => {
    if (!dateInput) return "";

    // If we get a plain "YYYY-MM-DD", treat it as local date to avoid UTC shifting.
    if (typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [y, m, d] = dateInput.split("-");
      return `${d}/${m}/${y}`;
    }

    const date =
      typeof dateInput === "string" || typeof dateInput === "number"
        ? new Date(dateInput)
        : dateInput;
    if (Number.isNaN(date?.getTime())) return dateInput;
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const displayOperationDate = formatToDDMMYYYY(
    operationDate || new Date()
  );

  const isFiltersEmpty =
    (filters.statuses?.length ?? 0) === 0 &&
    (filters.offices?.length ?? 0) === 0 &&
    !filters.query;

  const toggleArrayValue = (key, value) => {
    setFilters((prev) => {
      const existing = prev[key] || [];
      const next = existing.includes(value)
        ? existing.filter((v) => v !== value)
        : [...existing, value];
      return { ...prev, [key]: next };
    });
  };

  const handleApply = () => {
    const next = { ...filters };
    setFilters(next);
    if (onApply) onApply(next);
  };

  return (
    <div className="bg-gray-50 dark:bg-acs-dark-bg p-fluid-3 rounded-xl border border-gray-100 dark:border-acs-dark-border shadow-md w-full min-w-2xl max-h-[70vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-fluid-3">
        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-acs-dark-text">
          <span className="font-semibold">Operation Date:</span>
          <span className="text-gray-800 dark:text-acs-dark-text">
            {displayOperationDate}
          </span>
        </div>

        <div className="flex items-center gap-3 ml-6">
          <button
            onClick={handleApply}
            disabled={isFiltersEmpty}
            className={`flex items-center gap-1 text-sm font-medium rounded-md px-4 py-1.5 shadow-sm transition
              ${
                isFiltersEmpty
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#223688] text-white hover:bg-[#1a2c73] cursor-pointer"
              }`}
          >
            <RefreshCw className="w-4 h-4" strokeWidth={2} />
           Refresh
         </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-fluid-3">
        <div className="bg-white dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-lg p-fluid-2 shadow-sm">
          <div className="flex items-center gap-2 mb-fluid-2">
            <Building2 className="w-4 h-4 text-[#223688]" />
            <h3 className="text-fluid-base font-semibold text-gray-800 dark:text-acs-dark-text">
              Offices
            </h3>
          </div>
          <div className="flex flex-col gap-2 max-h-[36vh] overflow-y-auto pr-1">
            {offices.map((office) => (
              <label
                key={office}
                className="flex items-center gap-2 text-sm text-gray-700 dark:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-[#2A2D35] rounded-md px-2 py-1 cursor-pointer transition"
              >
                <input
                  type="checkbox"
                  checked={filters.offices?.includes(office)}
                  onChange={() => toggleArrayValue("offices", office)}
                  className="accent-[#223688] w-4 h-4 rounded-md"
                />
                <span className="flex items-center gap-1 w-full">
                  <span className="truncate">{office}</span>
                  {refreshingOffices?.includes(office) && (
                    <Loader2 className="ml-auto w-3 h-3 text-[#223688] animate-spin" />
                  )}
                </span>
              </label>
            ))}
            {offices.length === 0 && (
              <span className="text-xs text-gray-400">No offices available</span>
            )}
          </div>
        </div>

        {/* Removed Effective Date, Expiration Date, Sort By sections per request */}
      </div>
    </div>
  );
};

export default ContractDropdownContent;
